"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

interface Post {
  id: string;
  handle: string;
  name: string;
  avatar: string;
  avatarAlt: string;
  time: string;
  content: string;
  likes: number;
  retweets: number;
  platform: "twitter" | "threads";
  verified?: boolean;
}

const posts: Post[] = [
{
  id: "p1",
  handle: "@marcusthompson_nyc",
  name: "Marcus Thompson",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbf4bb47-1763293058500.png",
  avatarAlt: "Marcus Thompson, a man in his 30s with short dark hair smiling",
  time: "2h",
  content: "Just cancelled my @StreamFlixPremium subscription mid-billing cycle and switched to @ClearStream. Sent them a screenshot of the ad that triggered it. #PaidMeansPaid is working — they WILL notice the numbers.",
  likes: 3241,
  retweets: 891,
  platform: "twitter",
  verified: true
},
{
  id: "p2",
  handle: "@priya.streams",
  name: "Priya Mehta",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1241c5efb-1763301638353.png",
  avatarAlt: "Priya Mehta, young woman with dark hair and warm smile",
  time: "4h",
  content: "The audacity of charging $19.99/mo AND showing me 3 ads before an episode. I stamped their screenshot with the Red Stamp and posted it everywhere. This is not a drill. #PaidMeansPaid 🚫",
  likes: 1872,
  retweets: 445,
  platform: "threads"
},
{
  id: "p3",
  handle: "@devlin_watches",
  name: "Devlin O'Reilly",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12fffebcd-1770085317752.png",
  avatarAlt: "Devlin O'Reilly, man with beard and glasses looking thoughtful",
  time: "6h",
  content: "Used the @PaidMeansPaid video script for my TikTok. 140K views in 12 hours. The algorithm is on our side. Keep posting. Keep tagging. They're watching. #PaidMeansPaid",
  likes: 5103,
  retweets: 2240,
  platform: "twitter",
  verified: true
},
{
  id: "p4",
  handle: "@natasha.r.media",
  name: "Natasha Reynolds",
  avatar: "https://images.unsplash.com/photo-1660963423901-02f77332bd1e",
  avatarAlt: "Natasha Reynolds, woman with curly hair and confident expression",
  time: "8h",
  content: "My thread about the streaming double-dip got picked up by three consumer advocacy newsletters. The movement has its own momentum now. #PaidMeansPaid is bigger than any single brand.",
  likes: 2588,
  retweets: 673,
  platform: "threads"
},
{
  id: "p5",
  handle: "@jose_cuts_cords",
  name: "José Ramírez",
  avatar: "https://images.unsplash.com/photo-1596747768593-389e145d2e42",
  avatarAlt: "José Ramírez, man with short hair and friendly expression",
  time: "11h",
  content: "Went back to check — StreamFlix stock is down 2.3% this week. Correlation? We'll never know for sure. But we'll keep going. #PaidMeansPaid 📉",
  likes: 4412,
  retweets: 1190,
  platform: "twitter"
},
{
  id: "p6",
  handle: "@amara.digital",
  name: "Amara Osei",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a3bbaf5d-1770154803983.png",
  avatarAlt: "Amara Osei, woman with natural hair and bright smile",
  time: "14h",
  content: "The logic of #PaidMeansPaid is airtight. Free tier? Ads are the deal. Paid tier? You already paid. Running ads on a paid tier is a breach of the implicit contract. Simple. Spread it.",
  likes: 1934,
  retweets: 520,
  platform: "threads"
}];


function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export default function CommunitySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".community-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.remove("opacity-0-init");
                card.classList.add("animate-fade-up");
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="community"
      ref={sectionRef}
      className="bg-surface py-24 md:py-32 px-6 md:px-12 overflow-hidden"
      aria-label="Community wall of shame">
      
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-muted block mb-4">
              #PaidMeansPaid Live Feed
            </span>
            <h2 className="font-display text-5xl md:text-6xl font-black text-ink leading-[0.95] tracking-tightest">
              The Wall of<br />
              <span className="italic font-normal text-[#FF2222]">Shame.</span>
            </h2>
          </div>
          <p className="text-base text-mid max-w-sm font-light leading-relaxed">
            Real people. Real pressure. 214,000 advocates and counting. Every post is a vote.
          </p>
        </div>

        {/* Posts grid — masonry-style */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-0">
          {posts.map((post) =>
          <div
            key={post.id}
            className="community-card opacity-0-init social-card p-5 mb-5 break-inside-avoid">
            
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                    src={post.avatar}
                    alt={post.avatarAlt}
                    width={40}
                    height={40}
                    className="w-full h-full object-cover" />
                  
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-ink">{post.name}</span>
                      {post.verified &&
                    <Icon name="CheckBadgeIcon" size={14} variant="solid" className="text-[#FF2222]" />
                    }
                    </div>
                    <span className="text-xs text-muted">{post.handle}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted">
                  <span>{post.time}</span>
                  {post.platform === "twitter" ?
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-ink opacity-50" aria-label="Twitter/X">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg> :

                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-ink opacity-50" aria-label="Threads">
                      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.5 12.068c0-3.519.85-6.373 2.495-8.423 1.85-2.304 4.603-3.485 8.184-3.509h.014c2.718.018 5.036.827 6.888 2.404 1.73 1.468 2.868 3.48 3.379 5.98l-2.096.42c-.405-2.003-1.29-3.59-2.63-4.717-1.48-1.254-3.364-1.897-5.601-1.913-2.923.02-5.17.932-6.676 2.71C4.009 6.838 3.31 9.22 3.31 12.068c0 2.847.699 5.23 2.077 7.088 1.506 1.778 3.753 2.69 6.676 2.71 2.237-.016 4.12-.659 5.6-1.913 1.34-1.127 2.225-2.714 2.63-4.717l2.096.42c-.511 2.5-1.649 4.512-3.379 5.98-1.852 1.577-4.17 2.386-6.888 2.404h-.007z" />
                    </svg>
                }
                </div>
              </div>

              {/* Content */}
              <p className="text-sm text-ink leading-relaxed mb-4">{post.content}</p>

              {/* Engagement */}
              <div className="flex items-center gap-5 text-xs text-muted border-t border-[rgba(13,13,13,0.06)] pt-3">
                <span className="flex items-center gap-1.5">
                  <Icon name="HeartIcon" size={13} variant="outline" />
                  {formatCount(post.likes)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Icon name="ArrowPathRoundedSquareIcon" size={13} variant="outline" />
                  {formatCount(post.retweets)}
                </span>
                <span className="ml-auto text-[#FF2222] font-semibold">#PaidMeansPaid</span>
              </div>
            </div>
          )}
        </div>

        {/* CTA to join */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted mb-4 font-light">Post with #PaidMeansPaid and join the wall</p>
          <a
            href="https://twitter.com/intent/tweet?text=%23PaidMeansPaid%20%E2%80%94%20I%27m%20done%20being%20double-billed.%20Join%20the%20movement.%20PaidMeansPaid.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-ink text-ink px-6 py-3 rounded-full text-sm font-semibold hover:bg-ink hover:text-surface transition-all duration-300"
            aria-label="Add your voice to the community">
            
            <Icon name="PlusIcon" size={16} variant="outline" />
            Add Your Voice
          </a>
        </div>
      </div>
    </section>);

}